import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FUTEBOL_TEMPLATE_ID,
  FUTEBOL_VARIANTS,
  PALETTES,
  TEMAS,
  TEMPLATES_LIST,
  TEMPLATES,
  isFutebolTemplateId,
} from "../EventoPublico/templates";
import { defaultLayoutConfig, getBannerMode, getCoverImageMode, RSVP_FONT_OPTIONS } from "../../utils/layoutUtils";
import { getRsvpTheme } from "../../utils/rsvpTheme";
import { listarVariantes, criarVariante, atualizarVariante, deletarVariante } from "../../services/templateVariantService";
import AlertComponent from "../../components/AlertComponent";
import MockRsvpForm from "../../components/MockRsvpForm";
import ImageSourceField from "../../components/ImageSourceField";
import { Context } from "../../Reducers/Store";
import PreviewDeviceTabs from "../../components/PreviewDeviceTabs";
import { getInitialPreviewDevice, getPreviewDeviceConfig, withPreviewDevice } from "../../utils/previewDevices";

const MOCK_EVENTO = {
  nome: "Nome do evento",
  descricao: "1 Ano",
  dataDoEvento: "2026-06-15T16:00:00",
  dataLimiteConfirmacao: "2026-04-23T22:00:00",
  nomeEspaco: "Buffet Alegria",
  localizacao: "Rua das Flores, 123 - Belo Horizonte - MG",
  imagemUrl: null,
  mapaUrl: "https://maps.google.com",
  isActive: true,
};

// ─── Página principal ─────────────────────────────────────────────────────────

export default function LayoutsManager() {
  const [state] = useContext(Context);
  const navigate = useNavigate();
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [activeTemplateId, setActiveTemplateId] = useState(null);
  const [layoutCfg, setLayoutCfg] = useState(defaultLayoutConfig);
  const [variants, setVariants] = useState([]);
  const [selectedVariantId, setSelectedVariantId] = useState(null);
  const [variantNome, setVariantNome] = useState("");
  const [saving, setSaving] = useState(false);
  const [variantCounts, setVariantCounts] = useState({});
  const [variantsLoading, setVariantsLoading] = useState(false);
  const [variantsCache, setVariantsCache] = useState({});
  const [rsvpPreviewMode, setRsvpPreviewMode] = useState("form");
  const [device, setDevice] = useState(getInitialPreviewDevice);
  const templateRequestRef = useRef(null);

  useEffect(() => {
    if (!state?.profile?.podeCriarLayouts) {
      navigate("/eventos", { replace: true });
    }
  }, [navigate, state?.profile?.podeCriarLayouts]);

  // Carrega contagem de variantes por template ao abrir a lista
  useEffect(() => {
    const countsReady = TEMPLATES_LIST.every((t) =>
      Object.prototype.hasOwnProperty.call(variantCounts, t.id));
    if (selectedTemplate || countsReady) return undefined;

    const controller = new AbortController();
    let cancelled = false;

    async function loadCounts() {
      const counts = {};
      await Promise.allSettled(
        TEMPLATES_LIST.map(async (t) => {
          try {
            const data = await listarVariantes(t.id, { signal: controller.signal });
            counts[t.id] = data.length;
          } catch {
            if (!controller.signal.aborted) {
              counts[t.id] = 0;
            }
          }
        })
      );

      if (!cancelled && !controller.signal.aborted) {
        setVariantCounts((current) => ({ ...current, ...counts }));
      }
    }

    loadCounts();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [selectedTemplate, variantCounts]);

  const loadVariantsForTemplate = async (templateId, controller) => {
    const cachedVariants = variantsCache[templateId];

    setLayoutCfg(defaultLayoutConfig);
    setSelectedVariantId(null);
    setVariantNome("");
    setVariants(cachedVariants ?? []);
    setVariantsLoading(!cachedVariants);

    try {
      const data = await listarVariantes(templateId, { signal: controller.signal });
      if (controller.signal.aborted) return;

      setVariants(data);
      setVariantsCache((current) => ({ ...current, [templateId]: data }));
      setVariantCounts((current) => ({ ...current, [templateId]: data.length }));
    } catch {
      if (!controller.signal.aborted) {
        setVariants([]);
      }
    } finally {
      if (!controller.signal.aborted) {
        setVariantsLoading(false);
      }
      if (templateRequestRef.current === controller) {
        templateRequestRef.current = null;
      }
    }
  };

  const openTemplate = async (t) => {
    templateRequestRef.current?.abort();
    const controller = new AbortController();
    templateRequestRef.current = controller;

    setSelectedTemplate(t);
    setActiveTemplateId(t.id);
    await loadVariantsForTemplate(t.id, controller);
  };

  const selectTemplateFlavor = async (templateId) => {
    templateRequestRef.current?.abort();
    const controller = new AbortController();
    templateRequestRef.current = controller;
    setActiveTemplateId(templateId);
    await loadVariantsForTemplate(templateId, controller);
  };

  const handleSave = async () => {
    const templateId = activeTemplateId ?? selectedTemplate.id;
    setSaving(true);
    try {
      if (selectedVariantId) {
        const updated = await atualizarVariante(selectedVariantId, variantNome || undefined, layoutCfg);
        const nextVariants = variants.map(x => x.id === updated.id ? updated : x);
        setVariants(nextVariants);
        setVariantsCache(c => ({ ...c, [templateId]: nextVariants }));
        AlertComponent("Sucesso", "Variante atualizada!", { icon: "success" });
      } else {
        const created = await criarVariante(templateId, variantNome || null, layoutCfg);
        const nextVariants = [...variants, created];
        setVariants(nextVariants);
        setVariantsCache(c => ({ ...c, [templateId]: nextVariants }));
        setSelectedVariantId(created.id);
        setVariantNome(created.nome);
        setVariantCounts(c => ({ ...c, [templateId]: nextVariants.length }));
        AlertComponent("Sucesso", "Variante salva!", { icon: "success" });
      }
    } catch {
      AlertComponent("Erro", "Não foi possível salvar", { icon: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedVariantId || !window.confirm("Excluir esta variante?")) return;
    try {
      await deletarVariante(selectedVariantId);
      const templateId = activeTemplateId ?? selectedTemplate.id;
      const nextVariants = variants.filter(x => x.id !== selectedVariantId);
      setVariants(nextVariants);
      setVariantsCache(c => ({ ...c, [templateId]: nextVariants }));
      setVariantCounts(c => ({ ...c, [templateId]: nextVariants.length }));
      setSelectedVariantId(null);
      setVariantNome("");
      setLayoutCfg(defaultLayoutConfig);
    } catch {
      AlertComponent("Erro", "Não foi possível excluir", { icon: "error" });
    }
  };

  // ─── Tela de lista ───────────────────────────────────────────────────────────

  if (!selectedTemplate) {
    return (
      <div className="w-full min-w-0">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-slate-800">Layouts</h1>
          <p className="text-sm text-slate-500 mt-1">
            Crie e gerencie variantes visuais para cada template de convite.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {TEMPLATES_LIST.map(t => {
            const builtinCount = t.id === FUTEBOL_TEMPLATE_ID ? FUTEBOL_VARIANTS.length : 0;
            const savedCount = t.id === FUTEBOL_TEMPLATE_ID
              ? [FUTEBOL_TEMPLATE_ID, ...FUTEBOL_VARIANTS.map((variant) => variant.id)]
                  .reduce((sum, id) => sum + (variantCounts[id] ?? 0), 0)
              : (variantCounts[t.id] ?? 0);
            const hasCount = builtinCount > 0 || Object.prototype.hasOwnProperty.call(variantCounts, t.id);
            const count = savedCount + builtinCount;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => openTemplate(t)}
                className="bg-white rounded-xl border shadow-sm p-5 text-left hover:shadow-md hover:border-slate-300 transition-all group"
              >
                <div className="flex items-start gap-4">
                  <span className="text-4xl leading-none shrink-0">{t.emoji}</span>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-slate-800">{t.label}</p>
                    <p className="text-sm text-slate-500 mt-0.5">{t.desc}</p>
                    <div className="mt-3">
                      {!hasCount ? (
                        <span className="text-xs text-slate-400">Carregando variantes...</span>
                      ) : count > 0 ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium bg-violet-100 text-violet-700 px-2.5 py-0.5 rounded-full">
                          {count} variante{count > 1 ? "s" : ""}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400">Sem variantes</span>
                      )}
                    </div>
                  </div>
                  <svg className="w-5 h-5 text-slate-300 group-hover:text-slate-500 shrink-0 mt-1 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // ─── Tela de edição ──────────────────────────────────────────────────────────

  const previewTemplateId = activeTemplateId ?? selectedTemplate.id;
  const TemplateComponent = TEMPLATES[previewTemplateId];
  const previewDevice = getPreviewDeviceConfig(device);
  const previewLayoutConfig = withPreviewDevice(layoutCfg, device);

  return (
    <div className="w-full min-w-0">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          type="button"
          onClick={() => { setSelectedTemplate(null); setActiveTemplateId(null); }}
          className="p-2 rounded-lg hover:bg-gray-100 text-slate-600 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <span className="text-2xl leading-none">{selectedTemplate.emoji}</span>
        <div>
          <h1 className="text-xl font-semibold text-slate-800">{selectedTemplate.label}</h1>
          <p className="text-sm text-slate-500">{selectedTemplate.desc}</p>
        </div>
      </div>

      <div className="flex flex-col gap-6 items-start lg:flex-row lg:items-stretch lg:h-[calc(100dvh-13rem)] lg:min-h-0">

        {/* Coluna esquerda: controles */}
        <div className="w-full lg:w-80 xl:w-96 shrink-0 lg:min-h-0">
          <div className="space-y-4 lg:h-full lg:overflow-y-auto lg:pr-2">

            {/* Variantes salvas */}
            <div className="bg-white rounded-xl border shadow-sm p-4">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Variantes</h3>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => {
                    if (selectedTemplate.id === FUTEBOL_TEMPLATE_ID) {
                      selectTemplateFlavor(FUTEBOL_TEMPLATE_ID);
                    } else {
                      setSelectedVariantId(null);
                      setVariantNome("");
                      setLayoutCfg(defaultLayoutConfig);
                    }
                  }}
                  className={`px-3 py-1.5 text-sm rounded-md border transition-all ${
                    !selectedVariantId && (!isFutebolTemplateId(previewTemplateId) || previewTemplateId === FUTEBOL_TEMPLATE_ID)
                      ? "border-slate-900 bg-slate-900 text-white font-semibold"
                      : "border-gray-200 text-slate-600 hover:border-gray-400"
                  }`}
                >
                  {selectedTemplate.id === FUTEBOL_TEMPLATE_ID ? "São Paulo padrão" : "Padrão do tema"}
                </button>
                {selectedTemplate.id === FUTEBOL_TEMPLATE_ID && FUTEBOL_VARIANTS.map((variant) => (
                  <button
                    key={variant.id}
                    type="button"
                    onClick={() => selectTemplateFlavor(variant.id)}
                    className={`px-3 py-1.5 text-sm rounded-md border transition-all ${
                      !selectedVariantId && previewTemplateId === variant.id
                        ? "border-slate-900 bg-slate-900 text-white font-semibold"
                        : "border-gray-200 text-slate-600 hover:border-gray-400"
                    }`}
                  >
                    {variant.label}
                  </button>
                ))}
                {variants.map(v => (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => { setSelectedVariantId(v.id); setVariantNome(v.nome); setLayoutCfg(v.config); }}
                    className={`px-3 py-1.5 text-sm rounded-md border transition-all ${
                      String(selectedVariantId) === String(v.id)
                        ? "border-slate-900 bg-slate-900 text-white font-semibold"
                        : "border-gray-200 text-slate-600 hover:border-gray-400"
                    }`}
                  >
                    {v.nome}
                  </button>
                ))}
              </div>
              {variantsLoading && (
                <p className="text-xs text-slate-400 mt-3">Carregando variantes deste tema...</p>
              )}
            </div>

            {/* Controles de layout */}
            <LayoutControls layoutCfg={layoutCfg} setLayoutCfg={setLayoutCfg} templateId={previewTemplateId} />

            {/* Salvar */}
            <div className="bg-white rounded-xl border shadow-sm p-4">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">
                {selectedVariantId ? "Atualizar variante" : "Salvar como variante"}
              </h3>
              <input
                type="text"
                value={variantNome}
                onChange={e => setVariantNome(e.target.value)}
                placeholder='Ex: "Fundo escuro", "Sem endereço"'
                className="w-full p-2.5 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-slate-300 mb-3"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 px-4 py-2 bg-slate-900 text-white text-sm rounded-md hover:bg-slate-800 disabled:opacity-50 font-medium"
                >
                  {saving ? "Salvando..." : selectedVariantId ? "Atualizar" : "Salvar variante"}
                </button>
                {selectedVariantId && (
                  <button
                    type="button"
                    onClick={handleDelete}
                    className="px-3 py-2 border border-red-200 text-red-600 text-sm rounded-md hover:bg-red-50"
                  >
                    Excluir
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Coluna direita: preview ao vivo */}
        <div className="flex-1 min-w-0 w-full lg:min-h-0">
          <div className="bg-white rounded-xl border shadow-sm overflow-hidden flex flex-col min-h-[720px] lg:min-h-0 lg:h-full">
            <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between gap-3 text-sm">
              <span className="font-medium text-slate-700">Pré-visualização</span>
              <div className="flex flex-wrap items-center justify-end gap-3">
                <PreviewDeviceTabs value={device} onChange={setDevice} />
                <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1">
                  {[
                    { key: "form", label: "Formulário" },
                    { key: "invite", label: "Evento fechado" },
                    { key: "closed", label: "Prazo encerrado" },
                  ].map((option) => (
                    <button
                      key={option.key}
                      type="button"
                      onClick={() => setRsvpPreviewMode(option.key)}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                        rsvpPreviewMode === option.key
                          ? "bg-slate-900 text-white"
                          : "text-slate-500 hover:text-slate-700"
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                <span className="text-xs text-slate-400">Sem interação</span>
              </div>
            </div>
            <div className="flex-1 min-h-0 bg-gray-50 overflow-auto">
              <div className="min-h-full px-4 py-6 flex justify-center">
                <div
                  className={`bg-white shadow-lg ring-1 ring-slate-200 overflow-hidden transition-all duration-300 ${previewDevice.frameClass}`}
                  style={{
                    width: previewDevice.width,
                    maxWidth: previewDevice.maxWidth,
                    minHeight: previewDevice.minHeight,
                    pointerEvents: "none",
                    userSelect: "none",
                  }}
                >
                  {TemplateComponent && (
                    <TemplateComponent evento={MOCK_EVENTO} layoutConfig={previewLayoutConfig} templateId={previewTemplateId}>
                      <MockRsvpForm
                        templateId={previewTemplateId}
                        layoutConfig={previewLayoutConfig}
                        previewMode={rsvpPreviewMode}
                        closedDeadline={MOCK_EVENTO.dataLimiteConfirmacao}
                      />
                    </TemplateComponent>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

// ─── Controles de layout ──────────────────────────────────────────────────────

const NAMED_COLOR_VALUES = {
  black: "#000000",
  white: "#ffffff",
  transparent: "#ffffff",
};

const VISIBILITY_OPTIONS = [
  { value: "show", label: "Exibir" },
  { value: "none", label: "Não exibir" },
];

const ALIGNMENT_OPTIONS = [
  { value: "left", label: "Esquerda" },
  { value: "center", label: "Centro" },
  { value: "right", label: "Direita" },
  { value: "justify", label: "Justificado" },
];

function colorInputValue(value, fallback = "#000000") {
  if (typeof value !== "string" || !value.trim()) return fallback;
  const normalized = value.trim().toLowerCase();
  if (NAMED_COLOR_VALUES[normalized]) return NAMED_COLOR_VALUES[normalized];

  const hex = normalized.match(/#([0-9a-f]{3}|[0-9a-f]{6})\b/i)?.[0];
  if (hex) {
    if (hex.length === 4) {
      return `#${hex.slice(1).split("").map((char) => char + char).join("")}`;
    }
    return hex;
  }

  const rgb = normalized.match(/rgba?\(([^)]+)\)/);
  if (rgb) {
    const channels = rgb[1].split(",").slice(0, 3).map((part) => Number.parseFloat(part.trim()));
    if (channels.every((channel) => Number.isFinite(channel))) {
      return `#${channels.map((channel) => Math.max(0, Math.min(255, Math.round(channel))).toString(16).padStart(2, "0")).join("")}`;
    }
  }

  return fallback;
}

function firstColor(...values) {
  return values.find((value) => typeof value === "string" && value.trim()) ?? null;
}

export function LayoutControls({ layoutCfg, setLayoutCfg, templateId }) {
  const palette = PALETTES[templateId] ?? {};
  const rsvpThemeDefaults = getRsvpTheme(TEMAS[templateId] || TEMAS.original, null);
  const topDecoText = layoutCfg.topDecoText ?? "";
  const bottomDecoText = layoutCfg.bottomDecoText ?? "";
  const coverImageMode = getCoverImageMode(layoutCfg, defaultLayoutConfig.coverImageMode);
  const bannerMode = getBannerMode(layoutCfg);

  const setCoverImage = (key, val) =>
    setLayoutCfg(c => ({
      ...c,
      coverImage: { ...(c.coverImage ?? defaultLayoutConfig.coverImage), [key]: val },
    }));

  const setCoverImageUrl = (coverImageUrl) =>
    setLayoutCfg(c => ({ ...c, coverImageUrl: coverImageUrl || null }));

  const setCoverImageMode = (mode) =>
    setLayoutCfg(c => ({ ...c, coverImageMode: mode }));

  const setBannerUrl = (bannerUrl) =>
    setLayoutCfg(c => ({ ...c, bannerUrl: bannerUrl || null }));

  const setBannerMode = (mode) =>
    setLayoutCfg(c => ({ ...c, bannerMode: mode }));

  const setHeaderBgImageUrl = (headerBgImageUrl) =>
    setLayoutCfg(c => ({ ...c, headerBgImageUrl: headerBgImageUrl || null }));

  const setBg = (key, val) =>
    setLayoutCfg(c => {
      const background = c.background ?? defaultLayoutConfig.background;
      return { ...c, background: { ...background, [key]: val } };
    });

  const setSolid = (key, val) =>
    setLayoutCfg(c => {
      const background = c.background ?? defaultLayoutConfig.background;
      return { ...c, background: { ...background, solid: { ...background.solid, [key]: val } } };
    });

  const setGradient = (key, val) =>
    setLayoutCfg(c => {
      const background = c.background ?? defaultLayoutConfig.background;
      return { ...c, background: { ...background, gradient: { ...background.gradient, [key]: val } } };
    });

  const setBackgroundImage = (url) =>
    setLayoutCfg(c => {
      const background = c.background ?? defaultLayoutConfig.background;
      return { ...c, background: { ...background, image: { ...(background.image ?? defaultLayoutConfig.background.image), url: url || null } } };
    });

  const setStop = (idx, key, val) =>
    setLayoutCfg(c => {
      const background = c.background ?? defaultLayoutConfig.background;
      const gradient = background.gradient ?? defaultLayoutConfig.background.gradient;
      const stops = [...(gradient.stops ?? [])];
      stops[idx] = { ...stops[idx], [key]: val };
      return { ...c, background: { ...background, gradient: { ...gradient, stops } } };
    });

  const addStop = () =>
    setLayoutCfg(c => {
      const background = c.background ?? defaultLayoutConfig.background;
      const gradient = background.gradient ?? defaultLayoutConfig.background.gradient;
      const stops = [...(gradient.stops ?? [])];
      if (stops.length >= 4) return c;
      const sorted = [...stops].sort((a, b) => a.position - b.position);
      // Insere no maior intervalo
      let maxGap = -1, insertPos = 50;
      for (let i = 0; i < sorted.length - 1; i++) {
        const gap = sorted[i + 1].position - sorted[i].position;
        if (gap > maxGap) { maxGap = gap; insertPos = Math.round((sorted[i].position + sorted[i + 1].position) / 2); }
      }
      return { ...c, background: { ...background, gradient: { ...gradient, stops: [...stops, { color: "#aaaaaa", position: insertPos }] } } };
    });

  const removeStop = (idx) =>
    setLayoutCfg(c => {
      const background = c.background ?? defaultLayoutConfig.background;
      const gradient = background.gradient ?? defaultLayoutConfig.background.gradient;
      const stops = gradient.stops?.filter((_, i) => i !== idx) ?? [];
      return { ...c, background: { ...background, gradient: { ...gradient, stops } } };
    });

  const bg = layoutCfg.background ?? defaultLayoutConfig.background;
  const ci = layoutCfg.coverImage ?? defaultLayoutConfig.coverImage;
  const sec = layoutCfg.sections ?? defaultLayoutConfig.sections;

  const card = "rounded-xl border border-slate-200 bg-slate-50/80 p-4";
  const lbl = "block text-xs font-medium text-gray-600 mb-2";
  const btn = "px-3 py-1.5 text-sm rounded-md border transition-all";
  const on = "border-slate-900 bg-slate-900 text-white font-semibold";
  const off = "border-gray-200 text-slate-500 hover:border-gray-400";

  return (
    <>
      <ControlGroup
        title="Fundo geral"
        description="Edite o fundo externo do convite: use o padrão do tema, uma cor sólida, um gradiente ou uma imagem."
      >
        <div className={card}>
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Fundo da página</h3>
          <div className="flex flex-wrap gap-2 mb-4">
            {[["default", "Padrão"], ["solid", "Sólido"], ["gradient", "Gradiente"], ["image", "Imagem"]].map(([key, label]) => (
              <button key={key} type="button" onClick={() => setBg("type", key)}
                className={`${btn} ${bg.type === key ? on : off}`}>{label}</button>
            ))}
          </div>

          {bg.type === "solid" && (
            <div className="flex items-end gap-4">
              <div>
                <span className={lbl}>Cor</span>
                <input type="color" value={bg.solid?.color ?? "#ffffff"}
                  onChange={e => setSolid("color", e.target.value)}
                  className="w-12 h-10 rounded border cursor-pointer block" />
              </div>
              <div className="flex-1">
                <span className={lbl}>Opacidade: {bg.solid?.opacity ?? 100}%</span>
                <input type="range" min={10} max={100} value={bg.solid?.opacity ?? 100}
                  onChange={e => setSolid("opacity", parseInt(e.target.value))}
                  className="w-full accent-slate-900" />
              </div>
            </div>
          )}

          {bg.type === "gradient" && (
            <div className="space-y-3">
              <div>
                <span className={lbl}>Direção</span>
                <select value={bg.gradient?.direction ?? "to bottom"}
                  onChange={e => setGradient("direction", e.target.value)}
                  className="w-full p-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-300">
                  <option value="to bottom">Para baixo ↓</option>
                  <option value="to top">Para cima ↑</option>
                  <option value="to right">Para a direita →</option>
                  <option value="to left">Para a esquerda ←</option>
                  <option value="to bottom right">Diagonal ↘</option>
                  <option value="to bottom left">Diagonal ↙</option>
                  <option value="135deg">135°</option>
                </select>
              </div>
              <div className="space-y-2">
                {(bg.gradient?.stops ?? []).map((stop, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <input type="color" value={stop.color}
                      onChange={e => setStop(idx, "color", e.target.value)}
                      className="w-9 h-8 rounded border cursor-pointer shrink-0" />
                    <div className="flex-1">
                      <input type="range" min={0} max={100} value={stop.position}
                        onChange={e => setStop(idx, "position", parseInt(e.target.value))}
                        className="w-full accent-slate-900" />
                    </div>
                    <span className="text-xs text-slate-500 w-7 text-right">{stop.position}%</span>
                    {(bg.gradient?.stops?.length ?? 0) > 2 && (
                      <button type="button" onClick={() => removeStop(idx)}
                        className="text-red-400 hover:text-red-600 text-xl leading-none w-5">×</button>
                    )}
                  </div>
                ))}
              </div>
              {(bg.gradient?.stops?.length ?? 0) < 4 && (
                <button type="button" onClick={addStop}
                  className="text-sm text-slate-600 border rounded-md px-3 py-1.5 hover:bg-gray-50">
                  + Adicionar cor
                </button>
              )}
              <div className="h-8 rounded-lg border" style={{
                background: (bg.gradient?.stops?.length ?? 0) >= 2
                  ? `linear-gradient(${bg.gradient.direction}, ${bg.gradient.stops.map(s => `${s.color} ${s.position}%`).join(", ")})`
                  : "#f0f0f0"
              }} />
            </div>
          )}

          {bg.type === "image" && (
            <ImageSourceField
              value={bg.image?.url || ""}
              onChange={setBackgroundImage}
              categoria="layout-fundos"
              placeholder="Cole uma URL ou suba uma imagem"
              helpText="A imagem será centralizada, sem repetição, ocupando toda a tela."
              removeText="Remover imagem"
            />
          )}
        </div>

      </ControlGroup>

      <ControlGroup
        title="Cabeçalho"
        description="Ajuste as cores, textos e fundo do topo do convite."
      >
        {palette.topDeco && (
          <DecorativeTextControls
            card={card}
            lbl={lbl}
            layoutCfg={layoutCfg}
            setLayoutCfg={setLayoutCfg}
            title="Texto superior"
            visibilityTitle="Exibição do texto superior"
            alignmentTitle="Alinhamento do texto superior"
            colorTitle="Cor do texto superior"
            textKey="topDecoText"
            visibleKey="topDecoVisible"
            alignKey="topDecoAlign"
            colorKey="topDecoColor"
            value={topDecoText}
            placeholder={palette.topDeco.text ?? "Texto superior do tema"}
            helpText="Deixe vazio para usar o texto padrão do tema."
            defaultColorValue={palette.topDeco.color}
          />
        )}

        <ColorSection title="Faixa principal" card={card} lbl={lbl}
          bgKey="headerBg" textKey="headerTitleColor"
          defaultBgValue={palette.headerBg}
          defaultTextValue={palette.headerTitleColor}
          layoutCfg={layoutCfg} setLayoutCfg={setLayoutCfg} />

        {Object.keys(palette).length > 0 && (
          <HeaderTitleShadowControls
            card={card}
            lbl={lbl}
            layoutCfg={layoutCfg}
            setLayoutCfg={setLayoutCfg}
          />
        )}

        {Object.keys(palette).length > 0 && (
          <div className={card}>
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Imagem de fundo da faixa principal</h3>
            <ImageSourceField
              value={layoutCfg.headerBgImageUrl || ""}
              onChange={setHeaderBgImageUrl}
              categoria="layout-faixas"
              placeholder="Cole uma URL ou suba uma imagem"
              helpText="Deixe vazio para usar o fundo padrão ou a cor da faixa principal."
              removeText="Usar fundo padrão"
            />
          </div>
        )}

        {Object.keys(palette).length > 0 && (
          <HeaderBadgeControls
            card={card}
            lbl={lbl}
            btn={btn}
            on={on}
            off={off}
            palette={palette}
            layoutCfg={layoutCfg}
            setLayoutCfg={setLayoutCfg}
          />
        )}

        {Object.keys(palette).length > 0 && (
          <ColorSection title="Pílula de observação" card={card} lbl={lbl}
            bgKey="headerDescBg" textKey="headerDescColor"
            bgLabel="Cor da pílula" textLabel="Cor do texto"
            defaultBgValue={palette.headerDescBg}
            defaultTextValue={palette.headerDescColor}
            layoutCfg={layoutCfg} setLayoutCfg={setLayoutCfg} />
        )}
      </ControlGroup>

      <ControlGroup
        title="Foto de Capa"
        description="Defina a imagem ou faixa grande que aparece acima da data."
      >
        <div className={card}>
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Imagem acima da data</h3>

          <span className={lbl}>Origem</span>
          <div className="flex flex-wrap gap-2 mb-4">
            {[["default", "Padrão"], ["custom", "Personalizada"], ["none", "Nenhum"]].map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setBannerMode(key)}
                className={`${btn} ${bannerMode === key ? on : off}`}
              >
                {label}
              </button>
            ))}
          </div>

          {bannerMode === "custom" && (
            <ImageSourceField
              value={layoutCfg.bannerUrl || ""}
              onChange={setBannerUrl}
              categoria="layout-faixas"
              placeholder="Cole uma URL ou suba uma imagem"
              helpText="Esta imagem substitui a capa padrão do tema e aparece acima da data."
              removeText="Remover imagem"
            />
          )}

          {bannerMode === "default" && (
            <p className="text-xs text-slate-400">
              {palette.hasBanner
                ? "Usa a capa padrão do tema."
                : "Este tema não tem capa padrão. Use Personalizada para incluir uma imagem."}
            </p>
          )}

          {bannerMode === "none" && (
            <p className="text-xs text-slate-400">A imagem acima da data não será exibida neste layout.</p>
          )}
        </div>

      </ControlGroup>

      <ControlGroup
        title="Imagem principal"
        description="Controle a foto menor do cabeçalho quando o tema suportar uma imagem principal."
      >
        <div className={card}>
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Nome do evento</h3>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={layoutCfg.headerTitleVisible !== false}
              onChange={(e) => setLayoutCfg(c => ({ ...c, headerTitleVisible: e.target.checked ? null : false }))}
              className="w-4 h-4 accent-slate-900"
            />
            <span className="text-sm text-slate-700">Exibir nome do evento no convite</span>
          </label>
        </div>

        <div className={card}>
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Foto menor do cabeçalho</h3>

          <span className={lbl}>Origem</span>
          <div className="flex flex-wrap gap-2 mb-4">
            {[["default", "Padrão"], ["custom", "Personalizada"], ["none", "Nenhum"]].map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setCoverImageMode(key)}
                className={`${btn} ${coverImageMode === key ? on : off}`}
              >
                {label}
              </button>
            ))}
          </div>

          {coverImageMode === "custom" && (
            <ImageSourceField
              value={layoutCfg.coverImageUrl || ""}
              onChange={setCoverImageUrl}
              categoria="eventos-capas"
              placeholder="Cole uma URL ou suba uma imagem"
              helpText="Esta imagem substitui a imagem principal padrão do tema."
              removeText="Remover imagem"
            />
          )}

          {coverImageMode === "default" && (
            <p className="text-xs text-slate-400">
              {palette.coverImageUrl
                ? "Usa a imagem principal padrão do tema."
                : "Este tema não tem imagem principal padrão. Use Personalizada para incluir uma foto."}
            </p>
          )}

          {coverImageMode === "none" ? (
            <p className="text-xs text-slate-400">A foto de capa não será exibida neste layout.</p>
          ) : (
            <div className="mt-5 grid grid-cols-2 gap-4">
              <div>
                <span className={lbl}>Formato</span>
                <div className="flex flex-wrap gap-2">
                  {[["circle", "⬤ Círculo"], ["square", "⬛ Quadrado"]].map(([s, label]) => (
                    <button key={s} type="button" onClick={() => setCoverImage("shape", s)}
                      className={`${btn} ${ci.shape === s ? on : off}`}>{label}</button>
                  ))}
                </div>
              </div>
              <div>
                <span className={lbl}>Tamanho</span>
                <div className="flex flex-wrap gap-2">
                  {["P", "M", "G", "GG"].map(sz => (
                    <button key={sz} type="button" onClick={() => setCoverImage("size", sz)}
                      className={`${btn} ${ci.size === sz ? on : off}`}>{sz}</button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </ControlGroup>

      {palette.bottomDeco && (
        <ControlGroup
          title="Rodapé"
          description="Edite o texto ou emoticons que aparecem no fim do convite."
        >
          <DecorativeTextControls
            card={card}
            lbl={lbl}
            layoutCfg={layoutCfg}
            setLayoutCfg={setLayoutCfg}
            title="Texto do rodapé"
            visibilityTitle="Exibição do rodapé"
            alignmentTitle="Alinhamento do rodapé"
            colorTitle="Cor do rodapé"
            textKey="bottomDecoText"
            visibleKey="bottomDecoVisible"
            alignKey="bottomDecoAlign"
            colorKey="bottomDecoColor"
            value={bottomDecoText}
            placeholder={palette.bottomDeco.text ?? "Texto do rodapé do tema"}
            helpText="Deixe vazio para usar o rodapé padrão do tema."
            defaultColorValue={palette.bottomDeco.color}
          />
        </ControlGroup>
      )}

      <ControlGroup
        title="Data e Hora"
        description="Personalize o bloco da data e a divisória central entre os elementos."
      >
        <ColorSection title="Bloco da data e hora" card={card} lbl={lbl}
          bgKey="dateBg" textKey="dateTextColor"
          defaultBgValue={firstColor(palette.dateStripBg, palette.dateBoxBg)}
          defaultTextValue={firstColor(palette.dateTextColor, palette.dateDayColor, palette.dateTimeColor, palette.dateLabelColor)}
          layoutCfg={layoutCfg} setLayoutCfg={setLayoutCfg} />

        <SingleColorSection title="Cor da data" card={card} lbl={lbl}
          colorKey="dateDayColor" colorLabel="Cor da data"
          defaultColorValue={firstColor(palette.dateDayColor, palette.dateMonthColor, palette.dateWeekdayColor, palette.dateTextColor)}
          layoutCfg={layoutCfg} setLayoutCfg={setLayoutCfg} />

        <SingleColorSection title="Cor da hora" card={card} lbl={lbl}
          colorKey="dateTimeColor" colorLabel="Cor da hora"
          defaultColorValue={firstColor(palette.dateTimeColor, palette.dateTextColor)}
          layoutCfg={layoutCfg} setLayoutCfg={setLayoutCfg} />

        <SingleColorSection title="Divisória entre data e hora" card={card} lbl={lbl}
          colorKey="dateDividerColor" colorLabel="Cor da divisória vertical"
          defaultColorValue={palette.dateDivider}
          layoutCfg={layoutCfg} setLayoutCfg={setLayoutCfg} />
      </ControlGroup>

      <ControlGroup
        title="Endereço"
        description="Controle a aparência da seção de localização, do pin e da forma de exibição."
      >
        <ColorSection title="Bloco do endereço" card={card} lbl={lbl}
          bgKey="addrBg" textKey="addrTextColor"
          defaultBgValue={firstColor(palette.addrBg, palette.addrBoxBg)}
          defaultTextValue={palette.addrTextColor}
          layoutCfg={layoutCfg} setLayoutCfg={setLayoutCfg} />

        <SingleColorSection title="Linha acima do endereço" card={card} lbl={lbl}
          colorKey="addrDividerColor" colorLabel="Cor da linha"
          defaultColorValue={firstColor(palette.addrBorderTop, palette.dateDivider)}
          layoutCfg={layoutCfg} setLayoutCfg={setLayoutCfg} />

        <ChoiceSection
          title="Exibição do endereço"
          card={card}
          value={layoutCfg.addrDisplayMode}
          defaultValue={null}
          onChange={(value) => setLayoutCfg(c => ({ ...c, addrDisplayMode: value }))}
          options={[
            { value: "none", label: "Nenhum" },
            { value: "text", label: "Endereço" },
            { value: "pin", label: "Pin" },
            { value: "pinText", label: "Pin + endereço" },
          ]}
        />

        <ChoiceSection
          title="Posição do endereço escrito"
          card={card}
          value={layoutCfg.addrTextPosition}
          defaultValue={null}
          onChange={(value) => setLayoutCfg(c => ({ ...c, addrTextPosition: value }))}
          options={[
            { value: "top", label: "Acima" },
            { value: "bottom", label: "Abaixo" },
            { value: "left", label: "Esquerda" },
            { value: "right", label: "Direita" },
          ]}
          defaultLabel="Padrão (direita)"
        />

        <SingleColorSection title="Botão do pin" card={card} lbl={lbl}
          colorKey="addrPinBg" colorLabel="Fundo do pin"
          defaultColorValue={firstColor(palette.addrPinBg, palette.dateTimeColor, palette.dateDayColor)}
          layoutCfg={layoutCfg} setLayoutCfg={setLayoutCfg} />

        <ChoiceSection
          title="Borda do pin"
          card={card}
          value={layoutCfg.addrPinBorderMode}
          defaultValue={null}
          onChange={(value) => setLayoutCfg(c => ({ ...c, addrPinBorderMode: value }))}
          options={[
            { value: "none", label: "Sem borda" },
            { value: "custom", label: "Personalizada" },
          ]}
        />

        <SingleColorSection title="Cor da borda do pin" card={card} lbl={lbl}
          colorKey="addrPinBorderColor" colorLabel="Cor da borda"
          defaultColorValue={firstColor(palette.addrPinBorderColor, palette.addrTextColor)}
          layoutCfg={layoutCfg} setLayoutCfg={setLayoutCfg} />

        <ChoiceSection
          title="Alinhamento do endereço"
          card={card}
          value={layoutCfg.addrAlign}
          defaultValue={null}
          onChange={(value) => setLayoutCfg(c => ({ ...c, addrAlign: value }))}
          options={[
            { value: "left", label: "Esquerda" },
            { value: "center", label: "Centro" },
            { value: "right", label: "Direita" },
            { value: "justify", label: "Justificado" },
          ]}
          defaultLabel="Padrão (centro)"
        />

        <div className={card}>
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Waze no celular</h3>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={layoutCfg.addrShowWaze !== false}
              onChange={(e) => setLayoutCfg(c => ({ ...c, addrShowWaze: e.target.checked ? null : false }))}
              className="w-4 h-4 accent-slate-900"
            />
            <span className="text-sm text-slate-700">Exibir botão do Waze ao lado do pin</span>
          </label>
          <p className="text-xs text-slate-400 mt-2">
            Aparece apenas no celular e quando o pin estiver visível.
          </p>
        </div>

        <div className={card}>
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Nome do espaço</h3>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={layoutCfg.addrShowPlace !== false}
              onChange={(e) => setLayoutCfg(c => ({ ...c, addrShowPlace: e.target.checked ? null : false }))}
              className="w-4 h-4 accent-slate-900"
            />
            <span className="text-sm text-slate-700">Exibir nome do espaço</span>
          </label>
        </div>

        <ChoiceSection
          title="Posição do nome do espaço"
          card={card}
          value={layoutCfg.addrPlacePosition}
          defaultValue={null}
          onChange={(value) => setLayoutCfg(c => ({ ...c, addrPlacePosition: value }))}
          options={[
            { value: "top", label: "Acima" },
            { value: "bottom", label: "Abaixo" },
            { value: "left", label: "Esquerda" },
            { value: "right", label: "Direita" },
          ]}
          defaultLabel="Padrão (abaixo)"
        />

        <ChoiceSection
          title="Referência do nome do espaço"
          card={card}
          value={layoutCfg.addrPlaceReference}
          defaultValue={null}
          onChange={(value) => setLayoutCfg(c => ({ ...c, addrPlaceReference: value }))}
          options={[
            { value: "pin", label: "Pin" },
            { value: "text", label: "Endereço" },
            { value: "block", label: "Bloco inteiro" },
          ]}
          defaultLabel="Padrão (pin)"
        />

        <div className={card}>
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Visibilidade da seção</h3>
          <div className="space-y-2.5">
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox"
                checked={(sec.howToGet ?? true) && (sec.address ?? true)}
                onChange={e => setLayoutCfg(c => ({ ...c, sections: { ...c.sections, howToGet: e.target.checked, address: e.target.checked } }))}
                className="w-4 h-4 accent-slate-900" />
              <span className="text-sm text-slate-700">Endereço / Como chegar</span>
            </label>
          </div>
        </div>
      </ControlGroup>

      <ControlGroup
        title="Formulário de Confirmação"
        description="Organize o visual do RSVP, incluindo seções internas, campos e botões."
      >
        <ColorSection title="Base do formulário" card={card} lbl={lbl}
          bgKey="rsvpBg" textKey="rsvpTextColor"
          defaultBgValue={palette.rsvpBg}
          defaultTextValue={firstColor(rsvpThemeDefaults.formTextColor, rsvpThemeDefaults.labelColor)}
          layoutCfg={layoutCfg} setLayoutCfg={setLayoutCfg} />

        <SubgroupLabel
          title="Confirmar Pessoas"
          description="Estilize a área de convidados separadamente do bloco de recado."
        />

        <ColorSection title="Bloco confirmar pessoas" card={card} lbl={lbl}
          bgKey="rsvpPeopleBg" textKey="rsvpPeopleTextColor"
          bgLabel="Fundo do bloco" textLabel="Texto e labels"
          defaultBgValue={rsvpThemeDefaults.people?.sectionBg ?? rsvpThemeDefaults.sectionBg}
          defaultTextValue={rsvpThemeDefaults.people?.textColor ?? rsvpThemeDefaults.labelColor}
          layoutCfg={layoutCfg} setLayoutCfg={setLayoutCfg} />

        <SingleColorSection title="Título confirmar pessoas" card={card} lbl={lbl}
          colorKey="rsvpPeopleTitleColor" colorLabel="Cor do título"
          defaultColorValue={rsvpThemeDefaults.people?.titleColor ?? rsvpThemeDefaults.titulo?.color}
          layoutCfg={layoutCfg} setLayoutCfg={setLayoutCfg} />

        <SelectSection
          title="Fonte confirmar pessoas"
          card={card}
          label={lbl}
          value={layoutCfg.rsvpPeopleFontFamily}
          onChange={(value) => setLayoutCfg(c => ({ ...c, rsvpPeopleFontFamily: value }))}
          options={RSVP_FONT_OPTIONS}
          defaultLabel="Padrão do tema"
        />

        <ColorSection title="Campos confirmar pessoas" card={card} lbl={lbl}
          bgKey="rsvpPeopleInputBg" textKey="rsvpPeopleInputTextColor"
          bgLabel="Fundo do campo" textLabel="Texto digitado"
          defaultBgValue={rsvpThemeDefaults.people?.input?.background ?? rsvpThemeDefaults.inputExtra?.background}
          defaultTextValue={rsvpThemeDefaults.people?.input?.color ?? rsvpThemeDefaults.inputExtra?.color}
          layoutCfg={layoutCfg} setLayoutCfg={setLayoutCfg} />

        <SingleColorSection title="Placeholder confirmar pessoas" card={card} lbl={lbl}
          colorKey="rsvpPeopleInputPlaceholderColor" colorLabel="Cor do placeholder"
          defaultColorValue={rsvpThemeDefaults.people?.inputPlaceholderColor ?? rsvpThemeDefaults.inputPlaceholderColor}
          layoutCfg={layoutCfg} setLayoutCfg={setLayoutCfg} />

        <ColorSection title="Botão adicionar" card={card} lbl={lbl}
          bgKey="rsvpAddButtonBg" textKey="rsvpAddButtonTextColor"
          bgLabel="Fundo do botão" textLabel="Texto do botão"
          defaultBgValue={rsvpThemeDefaults.people?.addButton?.background ?? rsvpThemeDefaults.btnAdd?.background}
          defaultTextValue={rsvpThemeDefaults.people?.addButton?.color ?? rsvpThemeDefaults.btnAdd?.color}
          layoutCfg={layoutCfg} setLayoutCfg={setLayoutCfg} />

        <SubgroupLabel
          title="Recado"
          description="Defina cores e fonte próprias para a mensagem opcional."
        />

        <ColorSection title="Bloco do recado" card={card} lbl={lbl}
          bgKey="rsvpMessageBg" textKey="rsvpMessageTextColor"
          bgLabel="Fundo do bloco" textLabel="Texto do bloco"
          defaultBgValue={rsvpThemeDefaults.message?.sectionBg ?? rsvpThemeDefaults.recadoBg}
          defaultTextValue={rsvpThemeDefaults.message?.textColor ?? rsvpThemeDefaults.labelColor}
          layoutCfg={layoutCfg} setLayoutCfg={setLayoutCfg} />

        <SingleColorSection title="Título do recado" card={card} lbl={lbl}
          colorKey="rsvpMessageTitleColor" colorLabel="Cor do título"
          defaultColorValue={rsvpThemeDefaults.message?.titleColor ?? rsvpThemeDefaults.sectionTitleColor}
          layoutCfg={layoutCfg} setLayoutCfg={setLayoutCfg} />

        <SelectSection
          title="Fonte do recado"
          card={card}
          label={lbl}
          value={layoutCfg.rsvpMessageFontFamily}
          onChange={(value) => setLayoutCfg(c => ({ ...c, rsvpMessageFontFamily: value }))}
          options={RSVP_FONT_OPTIONS}
          defaultLabel="Padrão do tema"
        />

        <ColorSection title="Campo do recado" card={card} lbl={lbl}
          bgKey="rsvpMessageInputBg" textKey="rsvpMessageInputTextColor"
          bgLabel="Fundo do campo" textLabel="Texto digitado"
          defaultBgValue={rsvpThemeDefaults.message?.input?.background ?? rsvpThemeDefaults.inputExtra?.background}
          defaultTextValue={rsvpThemeDefaults.message?.input?.color ?? rsvpThemeDefaults.inputExtra?.color}
          layoutCfg={layoutCfg} setLayoutCfg={setLayoutCfg} />

        <SingleColorSection title="Placeholder do recado" card={card} lbl={lbl}
          colorKey="rsvpMessageInputPlaceholderColor" colorLabel="Cor do placeholder"
          defaultColorValue={rsvpThemeDefaults.message?.inputPlaceholderColor ?? rsvpThemeDefaults.inputPlaceholderColor}
          layoutCfg={layoutCfg} setLayoutCfg={setLayoutCfg} />

        <ColorSection title="Botão confirmar" card={card} lbl={lbl}
          bgKey="rsvpSubmitButtonBg" textKey="rsvpSubmitButtonTextColor"
          bgLabel="Fundo do botão" textLabel="Texto do botão"
          defaultBgValue={rsvpThemeDefaults.submit?.background}
          defaultTextValue={rsvpThemeDefaults.submit?.color}
          layoutCfg={layoutCfg} setLayoutCfg={setLayoutCfg} />
      </ControlGroup>

      <ControlGroup
        title="Prazo Encerrado"
        description="Personalize a mensagem exibida quando o período de confirmação já terminou."
      >
        <ColorSection title="Bloco do aviso" card={card} lbl={lbl}
          bgKey="rsvpClosedBg" textKey="rsvpClosedTextColor"
          bgLabel="Cor da seção" textLabel="Cor do texto do prazo"
          defaultBgValue={rsvpThemeDefaults.closedBg}
          defaultTextValue={rsvpThemeDefaults.closedTextColor}
          layoutCfg={layoutCfg} setLayoutCfg={setLayoutCfg} />

        <SingleColorSection title="Título do aviso" card={card} lbl={lbl}
          colorKey="rsvpClosedTitleColor" colorLabel="Cor do título"
          defaultColorValue={rsvpThemeDefaults.closedTitleColor}
          layoutCfg={layoutCfg} setLayoutCfg={setLayoutCfg} />

        <ChoiceSection
          title="Peso do título"
          card={card}
          value={layoutCfg.rsvpClosedTitleWeight}
          defaultValue={null}
          onChange={(value) => setLayoutCfg(c => ({ ...c, rsvpClosedTitleWeight: value }))}
          options={[
            { value: "normal", label: "Normal" },
            { value: "bold", label: "Bold" },
          ]}
          defaultLabel="Padrão do tema"
        />
      </ControlGroup>
    </>
  );
}

function ControlGroup({ title, description, children, defaultOpen = false }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <section className="bg-white rounded-2xl border shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        className="w-full px-4 py-4 bg-slate-50/80 hover:bg-slate-100/80 transition-colors text-left"
        aria-expanded={isOpen}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400 mb-1">Grupo</p>
            <h3 className="text-base font-semibold text-slate-800">{title}</h3>
            {description && (
              <p className="text-sm text-slate-500 mt-1">{description}</p>
            )}
          </div>
          <span
            className={`mt-1 shrink-0 rounded-full border border-slate-200 bg-white p-2 text-slate-500 transition-transform ${
              isOpen ? "rotate-180" : ""
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </span>
        </div>
      </button>
      {isOpen && (
        <div className="p-4 space-y-3 border-t border-slate-200">
          {children}
        </div>
      )}
    </section>
  );
}

function SubgroupLabel({ title, description }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
      <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
      {description && (
        <p className="text-xs text-slate-500 mt-1">{description}</p>
      )}
    </div>
  );
}

function DecorativeTextControls({
  card,
  lbl,
  layoutCfg,
  setLayoutCfg,
  title,
  visibilityTitle,
  alignmentTitle,
  colorTitle,
  textKey,
  visibleKey,
  alignKey,
  colorKey,
  value,
  placeholder,
  helpText,
  defaultColorValue,
}) {
  const visibilityValue = layoutCfg[visibleKey] === false
    ? "none"
    : layoutCfg[visibleKey] === true
      ? "show"
      : null;

  return (
    <>
      <div className={card}>
        <h3 className="text-sm font-semibold text-slate-700 mb-4">{title}</h3>
        <input
          type="text"
          value={value}
          onChange={(e) => setLayoutCfg(c => ({ ...c, [textKey]: e.target.value || null }))}
          placeholder={placeholder}
          className="w-full p-2.5 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-slate-300"
        />
        {helpText && <p className="text-xs text-slate-400 mt-2">{helpText}</p>}
      </div>

      <ChoiceSection
        title={visibilityTitle}
        card={card}
        value={visibilityValue}
        defaultValue={null}
        onChange={(choice) => setLayoutCfg(c => ({
          ...c,
          [visibleKey]: choice === "none" ? false : choice === "show" ? true : null,
        }))}
        options={VISIBILITY_OPTIONS}
        defaultLabel="Padrão (exibir)"
      />

      <ChoiceSection
        title={alignmentTitle}
        card={card}
        value={layoutCfg[alignKey]}
        defaultValue={null}
        onChange={(choice) => setLayoutCfg(c => ({ ...c, [alignKey]: choice }))}
        options={ALIGNMENT_OPTIONS}
        defaultLabel="Padrão (centro)"
      />

      <SingleColorSection
        title={colorTitle}
        card={card}
        lbl={lbl}
        colorKey={colorKey}
        colorLabel="Cor"
        defaultColorValue={defaultColorValue}
        layoutCfg={layoutCfg}
        setLayoutCfg={setLayoutCfg}
      />
    </>
  );
}

function HeaderTitleShadowControls({ card, lbl, layoutCfg, setLayoutCfg }) {
  const mode = layoutCfg.headerTitleShadowMode ?? "default";
  const btn = "px-3 py-1.5 text-sm rounded-md border transition-all";
  const on = "border-slate-900 bg-slate-900 text-white font-semibold";
  const off = "border-gray-200 text-slate-500 hover:border-gray-400";
  const setShadowValue = (key, value) => setLayoutCfg(c => ({ ...c, [key]: value }));
  const setNumber = (key, value) => {
    const next = value === "" ? null : Number(value);
    setShadowValue(key, Number.isFinite(next) ? next : null);
  };
  const resetShadow = () => setLayoutCfg(c => ({
    ...c,
    headerTitleShadowMode: null,
    headerTitleShadowColor: null,
    headerTitleShadowOffsetX: null,
    headerTitleShadowOffsetY: null,
    headerTitleShadowBlur: null,
  }));

  return (
    <div className={card}>
      <div className="flex items-center justify-between gap-3 mb-4">
        <h3 className="text-sm font-semibold text-slate-700">Sombra do nome do evento</h3>
        {mode !== "default" && (
          <button type="button" onClick={resetShadow} className="text-xs text-slate-500 hover:text-slate-700">
            ↺ padrão
          </button>
        )}
      </div>

      <span className={lbl}>Exibição</span>
      <div className="flex flex-wrap gap-2 mb-4">
        {[
          ["default", "Padrão"],
          ["none", "Sem sombra"],
          ["custom", "Personalizada"],
        ].map(([value, label]) => (
          <button
            key={value}
            type="button"
            onClick={() => setShadowValue("headerTitleShadowMode", value === "default" ? null : value)}
            className={`${btn} ${mode === value ? on : off}`}
          >
            {label}
          </button>
        ))}
      </div>

      {mode === "custom" && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <span className={lbl}>Cor</span>
            <input
              type="color"
              value={colorInputValue(layoutCfg.headerTitleShadowColor, "#000000")}
              onChange={(e) => setShadowValue("headerTitleShadowColor", e.target.value)}
              className="w-12 h-10 rounded border cursor-pointer block"
            />
          </div>
          <div>
            <span className={lbl}>Desfoque</span>
            <input
              type="number"
              min="0"
              max="40"
              value={layoutCfg.headerTitleShadowBlur ?? 8}
              onChange={(e) => setNumber("headerTitleShadowBlur", e.target.value)}
              className="w-full p-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
            />
          </div>
          <div>
            <span className={lbl}>Horizontal</span>
            <input
              type="number"
              min="-40"
              max="40"
              value={layoutCfg.headerTitleShadowOffsetX ?? 0}
              onChange={(e) => setNumber("headerTitleShadowOffsetX", e.target.value)}
              className="w-full p-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
            />
          </div>
          <div>
            <span className={lbl}>Vertical</span>
            <input
              type="number"
              min="-40"
              max="40"
              value={layoutCfg.headerTitleShadowOffsetY ?? 2}
              onChange={(e) => setNumber("headerTitleShadowOffsetY", e.target.value)}
              className="w-full p-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
            />
          </div>
        </div>
      )}
    </div>
  );
}

function HeaderBadgeControls({ card, lbl, btn, on, off, palette, layoutCfg, setLayoutCfg }) {
  const mode = layoutCfg.badgeImageMode ?? "default";
  const setBadgeValue = (key, value) => setLayoutCfg(c => ({ ...c, [key]: value }));
  const setBadgeDefaultable = (key, value) => setBadgeValue(key, value === "default" ? null : value);
  const showBadgeSettings = mode !== "none";

  return (
    <div className={card}>
      <h3 className="text-sm font-semibold text-slate-700 mb-4">Imagem/escudo do topo</h3>

      <span className={lbl}>Origem</span>
      <div className="flex flex-wrap gap-2 mb-4">
        {[
          ["default", "Padrão"],
          ["custom", "Personalizada"],
          ["none", "Nenhum"],
        ].map(([value, label]) => (
          <button
            key={value}
            type="button"
            onClick={() => setBadgeDefaultable("badgeImageMode", value)}
            className={`${btn} ${mode === value ? on : off}`}
          >
            {label}
          </button>
        ))}
      </div>

      {mode === "custom" && (
        <ImageSourceField
          value={layoutCfg.badgeImageUrl || ""}
          onChange={(badgeImageUrl) => setBadgeValue("badgeImageUrl", badgeImageUrl || null)}
          categoria="layout-emblemas"
          placeholder="Cole uma URL ou suba uma imagem"
          helpText="Substitui o escudo/imagem padrão que aparece no topo do cabeçalho."
          removeText="Remover imagem"
        />
      )}

      {mode === "default" && (
        <p className="text-xs text-slate-400 mb-4">
          {palette.teamBadgeUrl
            ? "Usa a imagem padrão do tema."
            : "Este tema não tem imagem padrão no topo. Use Personalizada para incluir uma imagem."}
        </p>
      )}

      {showBadgeSettings && (
        <div className="space-y-4 mt-4">
          <div>
            <span className={lbl}>Posição</span>
            <div className="flex flex-wrap gap-2">
              {[
                ["default", "Padrão"],
                ["top", "Topo"],
                ["afterImage", "Abaixo da foto"],
                ["beforeTitle", "Acima do nome"],
                ["afterTitle", "Abaixo do nome"],
              ].map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setBadgeDefaultable("badgeImagePlacement", value)}
                  className={`${btn} ${((layoutCfg.badgeImagePlacement ?? "default") === value) ? on : off}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <span className={lbl}>Alinhamento</span>
            <div className="flex flex-wrap gap-2">
              {[
                ["default", "Padrão"],
                ["left", "Esquerda"],
                ["center", "Centro"],
                ["right", "Direita"],
              ].map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setBadgeDefaultable("badgeImageAlign", value)}
                  className={`${btn} ${((layoutCfg.badgeImageAlign ?? "default") === value) ? on : off}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <span className={lbl}>Tamanho</span>
            <div className="flex flex-wrap gap-2">
              {["default", "P", "M", "G", "GG"].map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setBadgeDefaultable("badgeImageSize", value)}
                  className={`${btn} ${((layoutCfg.badgeImageSize ?? "default") === value) ? on : off}`}
                >
                  {value === "default" ? "Padrão" : value}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {mode === "none" && (
        <p className="text-xs text-slate-400">A imagem/escudo do topo não será exibida neste layout.</p>
      )}
    </div>
  );
}

// ─── Color section reutilizável ──────────────────────────────────────────────

function ColorSection({
  title,
  card,
  lbl,
  bgKey,
  textKey,
  layoutCfg,
  setLayoutCfg,
  bgLabel = "Cor de fundo",
  textLabel = "Cor do texto",
  defaultBgValue = "#ffffff",
  defaultTextValue = "#000000",
}) {
  const bgVal   = layoutCfg[bgKey];
  const textVal = layoutCfg[textKey];
  const setVal  = (key, val) => setLayoutCfg(c => ({ ...c, [key]: val }));
  const bgInputValue = colorInputValue(bgVal, colorInputValue(defaultBgValue, "#ffffff"));
  const textInputValue = colorInputValue(textVal, colorInputValue(defaultTextValue, "#000000"));

  return (
    <div className={card}>
      <h3 className="text-sm font-semibold text-slate-700 mb-4">{title}</h3>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <span className={lbl}>{bgLabel}</span>
          <div className="flex items-center gap-2">
            <input type="color" value={bgInputValue}
              onChange={e => setVal(bgKey, e.target.value)}
              className="w-12 h-10 rounded border cursor-pointer block" />
            {bgVal && (
              <button type="button" onClick={() => setVal(bgKey, null)}
                className="text-xs text-slate-500 hover:text-slate-700">↺ padrão</button>
            )}
          </div>
        </div>
        <div>
          <span className={lbl}>{textLabel}</span>
          <div className="flex items-center gap-2">
            <input type="color" value={textInputValue}
              onChange={e => setVal(textKey, e.target.value)}
              className="w-12 h-10 rounded border cursor-pointer block" />
            {textVal && (
              <button type="button" onClick={() => setVal(textKey, null)}
                className="text-xs text-slate-500 hover:text-slate-700">↺ padrão</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SingleColorSection({ title, card, lbl, colorKey, colorLabel, layoutCfg, setLayoutCfg, defaultColorValue = "#9ca3af" }) {
  const value = layoutCfg[colorKey];
  const setVal = (key, val) => setLayoutCfg(c => ({ ...c, [key]: val }));
  const inputValue = colorInputValue(value, colorInputValue(defaultColorValue, "#9ca3af"));

  return (
    <div className={card}>
      <h3 className="text-sm font-semibold text-slate-700 mb-4">{title}</h3>
      <div>
        <span className={lbl}>{colorLabel}</span>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={inputValue}
            onChange={e => setVal(colorKey, e.target.value)}
            className="w-12 h-10 rounded border cursor-pointer block"
          />
          {value && (
            <button
              type="button"
              onClick={() => setVal(colorKey, null)}
              className="text-xs text-slate-500 hover:text-slate-700"
            >
              ↺ padrão
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ChoiceSection({ title, card, value, defaultValue = null, defaultLabel = "Padrão", onChange, options }) {
  const btn = "px-3 py-1.5 text-sm rounded-md border transition-all";
  const on = "border-slate-900 bg-slate-900 text-white font-semibold";
  const off = "border-gray-200 text-slate-500 hover:border-gray-400";

  return (
    <div className={card}>
      <h3 className="text-sm font-semibold text-slate-700 mb-4">{title}</h3>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => onChange(defaultValue)}
          className={`${btn} ${value === null || value === undefined ? on : off}`}
        >
          {defaultLabel}
        </button>
        {options.map(option => (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            className={`${btn} ${value === option.value ? on : off}`}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function SelectSection({ title, card, label, value, onChange, options, defaultLabel = "Padrão" }) {
  return (
    <div className={card}>
      <h3 className="text-sm font-semibold text-slate-700 mb-4">{title}</h3>
      <div>
        <span className={label}>Fonte</span>
        <select
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value || null)}
          className="w-full p-2.5 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-300 bg-white"
        >
          <option value="">{defaultLabel}</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
